/**
 * NewsListView - full-page vertical list for the "See All" links from
 * each dashboard news tier. One component, one route per slug.
 *
 * Uses the same NewsCard component as the horizontal tiers - the card
 * is width-fluid at this level thanks to a wrapper that lets it grow.
 * Infinite scroll via IntersectionObserver on the sentinel; 50-item
 * page size matches the backend `limit` cap.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import axios from "axios";
import { useAuth, authHeaders } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import NewsCard from "../components/dashboard/NewsCard";
import PlayerSearchOverlay from "../components/dashboard/PlayerSearchOverlay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TITLES = {
  "my-players": "My Players",
  "my-leagues": "My Leagues",
  "nfl":        "NFL News",
};

const EMPTY_CTAS = {
  "my-players": "Join a league to see personalized player news.",
  "my-leagues": "Create or join a league to see activity.",
  "nfl":        "News updates during the NFL season.",
};

const PAGE_SIZE = 50;

export default function NewsListView() {
  const { slug } = useParams();  // "my-players" | "my-leagues" | "nfl"
  const nav = useNavigate();
  useAuth();
  const { isDark } = useTheme();
  const bg = isDark ? "bg-[#111111]" : "bg-[#F0F0F0]";
  const fg = isDark ? "text-[#F5F5F5]" : "text-[#111111]";
  const muted = isDark ? "text-[#A0A0A0]" : "text-[#606060]";

  const [items, setItems] = useState([]);
  const [offset, setOffset] = useState(0);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const sentinelRef = useRef(null);

  const loadMore = useCallback(async () => {
    if (loading || done) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/news/${slug}?limit=${PAGE_SIZE}&offset=${offset}`,
        { headers: authHeaders() },
      );
      const next = data.items || [];
      setItems((prev) => [...prev, ...next]);
      setOffset((prev) => prev + next.length);
      if (next.length < PAGE_SIZE) setDone(true);
    } catch { setDone(true); }
    finally { setLoading(false); }
  }, [slug, offset, loading, done]);

  // Reset when slug changes.
  useEffect(() => {
    setItems([]); setOffset(0); setDone(false);
  }, [slug]);

  // Initial + infinite scroll.
  useEffect(() => {
    if (items.length === 0 && !done && !loading) loadMore();
  }, [items.length, done, loading, loadMore]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) loadMore();
    }, { rootMargin: "300px" });
    io.observe(el);
    return () => io.disconnect();
  }, [loadMore]);

  const title = TITLES[slug] || "News";
  const emptyCTA = EMPTY_CTAS[slug] || "No news available.";

  return (
    <div className={`min-h-screen ${bg} ${fg}`}>
      <PlayerSearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <header className={`border-b ${isDark ? "border-[#2A2A2A] bg-[#141414]" : "border-[#D0D0D0] bg-white"} sticky top-0 z-30`}>
        <div className="max-w-4xl mx-auto flex items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={() => nav("/dashboard")}
            className={`p-2 rounded-lg hover:bg-[#FF6B00]/10 ${muted}`}
            data-testid="news-list-back"
            aria-label="Back to dashboard"
          >
            <ChevronLeft size={18} />
          </button>
          <h1 className="font-display font-black uppercase text-lg tracking-tight">{title}</h1>
          {items.length > 0 && (
            <span className={`text-xs ${muted}`}>({items.length}{done ? "" : "+"})</span>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {items.length === 0 && done ? (
          <div className={`rounded-xl border border-dashed ${isDark ? "border-[#2A2A2A] bg-[#141414]" : "border-[#D0D0D0] bg-white"} p-8 text-center`}>
            <p className={`text-sm ${muted}`}>{emptyCTA}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="news-list-grid">
            {items.map((it) => (
              <NewsCard
                key={it.id}
                item={it}
                onInternalClick={() => {}}
                onPlayerClick={() => setSearchOpen(true)}
              />
            ))}
          </div>
        )}
        <div ref={sentinelRef} className="h-8" />
        {loading && (
          <div className={`text-center py-4 text-xs ${muted}`}>Loading…</div>
        )}
      </main>
    </div>
  );
}
